// src/components/mdx/utils/component-utils.shared.tsx
//
// Server-SAFE MDX component utilities (no "use client"): the throttled error
// logger, the pure presentational loading/error fallbacks, and the heading /
// wrapper / prop helpers. Imported directly by the server entry
// (component-utils.server.tsx) and composed by the client entry
// (component-utils.client.tsx), which adds the `logFallbackTrigger` side-effect
// and the client-only Suspense/ErrorBoundary `createSafeMDXComponent`. This is
// the single home for the markup both entries used to duplicate (B2).

import type { ComponentType, FunctionComponent, ReactNode, HTMLAttributes } from 'react'
import { cn } from '~/lib/utils'
import { logClientError } from '~/lib/logger-client'

/**
 * ==========================================
 * ERROR LOGGING UTILITIES
 * ==========================================
 */

/**
 * Throttles error logging to prevent console flooding.
 * Multiple errors from the same component with the same message will be logged
 * at most once every 5 seconds.
 * 
 * @type {Map<string, number>} Map of error keys to timestamp of last log
 */
const errorLogThrottles = new Map<string, number>()

/**
 * Logs component errors with throttling to prevent flooding the console.
 * 
 * @param {string} componentName - The name of the component that failed.
 * @param {Error} error - The error that was thrown.
 * @returns {void}
 * @example
 * try {
 *   // Component code
 * } catch (error) {
 *   logComponentError('MyComponent', error as Error)
 * }
 */
export function logComponentError(componentName: string, error: Error): void {
  const key = `${componentName}:${error.message}`
  const now = Date.now()
  const lastLogged = errorLogThrottles.get(key) ?? 0
  
  // Only log if we haven't logged this exact error in the last 5 seconds
  // This prevents overwhelming the console with repeated errors
  if (now - lastLogged > 5000) {
    // Route through the client drain (no-ops server-side; this util is
    // client-reachable). The 5s throttle still bounds repeated identical errors.
    logClientError(error, {
      component: componentName,
      context: 'Error in MDX component',
      type: 'MDX_COMPONENT_ERROR',
    })
    errorLogThrottles.set(key, now)
  }
}

/**
 * ==========================================
 * FALLBACK COMPONENTS
 * ==========================================
 * Loading states for different component types
 */

/**
 * Image fallback loading state
 * @returns {JSX.Element} - The image loading fallback component
 */
export const ImageFallback = (): JSX.Element => (
  <div className="w-full h-64 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center animate-pulse">
    <div className="text-gray-400 dark:text-gray-600">
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    </div>
  </div>
)

/**
 * Quote fallback loading state
 * @returns {JSX.Element} - The quote loading fallback component
 */
export const QuoteFallback = (): JSX.Element => (
  <div className="w-full p-6 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse">
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
  </div>
)

/**
 * Chart fallback loading state
 * @returns {JSX.Element} - The chart loading fallback component
 */
export const ChartFallback = (): JSX.Element => (
  <div className="w-full h-80 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center animate-pulse">
    <div className="text-gray-400 dark:text-gray-600">
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    </div>
  </div>
)

/**
 * Video fallback loading state
 * @returns {JSX.Element} - The video loading fallback component
 */
export const VideoFallback = (): JSX.Element => (
  <div className="w-full h-80 bg-gray-100 dark:bg-gray-800 rounded-md flex items-center justify-center animate-pulse">
    <div className="text-gray-400 dark:text-gray-600">
      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
  </div>
)

/**
 * Card fallback loading state
 * @returns {JSX.Element} - The card loading fallback component
 */
export const CardFallback = (): JSX.Element => (
  <div className="w-full p-6 bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse">
    <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
  </div>
)

/**
 * Table fallback loading state
 * @returns {JSX.Element} - The table loading fallback component
 */
export const TableFallback = (): JSX.Element => (
  <div className="w-full overflow-hidden bg-gray-100 dark:bg-gray-800 rounded-md animate-pulse">
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
    </div>
    <div className="p-4">
      <div className="space-y-3">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
      </div>
    </div>
  </div>
)

/**
 * Metrics fallback loading state
 * @returns {JSX.Element} - The metrics loading fallback component
 */
export const MetricsFallback = (): JSX.Element => {
  const items = Array.from({ length: 3 }, (_, i) => (
    <div key={i} className="p-6 bg-gray-100 dark:bg-gray-800 rounded-md">
      <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
      <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
    </div>
  ))
  
  return (
    <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {items}
    </div>
  )
}

/**
 * Callout fallback loading state
 * @returns {JSX.Element} - The callout loading fallback component
 */
export const CalloutFallback = (): JSX.Element => (
  <div className="w-full p-4 my-4 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 rounded-md animate-pulse">
    <div className="flex items-start">
      <div className="flex-shrink-0 mt-1">
        <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
      </div>
      <div className="ml-3 w-full">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
      </div>
    </div>
  </div>
)

/**
 * Grid layout fallback loading state
 * @returns {JSX.Element} - The grid layout loading fallback component
 */
export const GridFallback = (): JSX.Element => {
  const items = Array.from({ length: 6 }, (_, i) => (
    <div key={i} className="p-4 bg-gray-100 dark:bg-gray-800 rounded-md">
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-3"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
    </div>
  ))
  
  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 animate-pulse">
      {items}
    </div>
  )
}

/**
 * Card grid fallback loading state
 * @returns {JSX.Element} - The card grid loading fallback component
 */
export const CardGridFallback = (): JSX.Element => {
  const items = Array.from({ length: 3 }, (_, i) => (
    <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="h-40 bg-gray-200 dark:bg-gray-700"></div>
      <div className="p-4">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3"></div>
      </div>
    </div>
  ))
  
  return (
    <div className="w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
      {items}
    </div>
  )
}

/**
 * Flex layout fallback loading state
 * @returns {JSX.Element} - The flex layout loading fallback component
 */
export const FlexFallback = (): JSX.Element => (
  <div className="w-full flex flex-col md:flex-row gap-4 animate-pulse">
    <div className="flex-1 p-4 bg-gray-100 dark:bg-gray-800 rounded-md">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6 mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
    </div>
    <div className="flex-1 p-4 bg-gray-100 dark:bg-gray-800 rounded-md">
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6 mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
    </div>
  </div>
)

/**
 * Tabs fallback loading state
 * @returns {JSX.Element} - The tabs loading fallback component
 */
export const TabsFallback = (): JSX.Element => {
  const tabItems = Array.from({ length: 3 }, (_, i) => (
    <div key={i} className={`py-2 px-4 ${i === 0 ? 'border-b-2 border-blue-500 dark:border-blue-400' : ''}`}>
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
    </div>
  ))
  
  return (
    <div className="w-full animate-pulse">
      <div className="border-b border-gray-200 dark:border-gray-700 flex mb-4">
        {tabItems}
      </div>
      <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-md">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6 mb-2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
      </div>
    </div>
  )
}

/**
 * Interactive component fallback loading state
 * @returns {JSX.Element} - The interactive component loading fallback
 */
export const InteractiveFallback = (): JSX.Element => (
  <div className="w-full border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden animate-pulse">
    <div className="p-4 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center">
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
        <div className="flex space-x-2">
          <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
          <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    </div>
    <div className="p-6 bg-white dark:bg-gray-800">
      <div className="space-y-4">
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
        <div className="flex justify-end space-x-2">
          <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
          <div className="h-9 bg-blue-200 dark:bg-blue-800/30 rounded w-24"></div>
        </div>
      </div>
    </div>
  </div>
)

/**
 * Accordion fallback loading state
 * @returns {JSX.Element} - The accordion loading fallback component
 */
export const AccordionFallback = (): JSX.Element => {
  const items = Array.from({ length: 3 }, (_, i) => (
    <div key={i} className="p-4">
      <div className="flex justify-between items-center">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
        <div className="h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
      </div>
      {i === 0 && (
        <div className="mt-3">
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
        </div>
      )}
    </div>
  ))
  
  return (
    <div className="w-full divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden animate-pulse">
      {items}
    </div>
  )
}

/**
 * Timeline fallback loading state
 * @returns {JSX.Element} - The timeline loading fallback component
 */
export const TimelineFallback = (): JSX.Element => {
  const items = Array.from({ length: 3 }, (_, i) => (
    <div key={i} className="mb-6 relative">
      <div className="absolute left-[-1.625rem] w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700"></div>
      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-3"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2"></div>
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
    </div>
  ))
  
  return (
    <div className="w-full relative pl-8 animate-pulse">
      <div className="absolute top-0 bottom-0 left-3 w-px bg-gray-200 dark:bg-gray-700"></div>
      {items}
    </div>
  )
}

/**
 * Flowchart fallback loading state
 * @returns {JSX.Element} - The flowchart loading fallback component
 */
export const FlowchartFallback = (): JSX.Element => (
  <div className="w-full h-96 bg-gray-100 dark:bg-gray-800 rounded-md my-4 animate-pulse"
       aria-label="Loading flowchart diagram..."
       role="status" />
)

/**
 * Text content fallback loading state
 * @returns {JSX.Element} - The text content loading fallback component
 */
export const TextFallback = (): JSX.Element => (
  <div className="w-full space-y-3 animate-pulse">
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5"></div>
    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
  </div>
)

/**
 * Text inline fallback designed for inline text components to avoid invalid HTML nesting
 * Uses span instead of div to ensure it can be safely placed inside paragraph tags
 * 
 * @returns {JSX.Element} Inline text fallback component
 */
export const TextInlineFallback = (): JSX.Element => (
  <span className="inline-block animate-pulse bg-gray-200 dark:bg-gray-700 rounded px-2 py-1 text-sm">
    <span className="sr-only">Loading inline component...</span>
  </span>
)

/**
 * Link fallback loading state
 * Minimal fallback for links that won't disrupt text flow
 * 
 * @returns {JSX.Element} - The link loading fallback component
 */
export const LinkFallback = (): JSX.Element => (
  <span className="inline-block animate-pulse bg-blue-100 dark:bg-blue-900/20 text-transparent rounded px-1">
    <span className="sr-only">Loading link...</span>
    loading link
  </span>
)

/**
 * Provider fallback loading state
 * Invisible fallback for context providers and non-UI components
 * 
 * @returns {JSX.Element} - The provider loading fallback component
 */
export const ProviderFallback = (): JSX.Element => (
  <span className="sr-only">Loading provider...</span>
)

/**
 * Utility fallback loading state
 * For non-visual utility components that shouldn't show a loading state
 * 
 * @returns {JSX.Element} - The utility loading fallback component
 */
export const UtilityFallback = (): JSX.Element => (
  <span className="sr-only">Loading utility component...</span>
)

/**
 * ==========================================
 * ERROR FALLBACK COMPONENTS
 * ==========================================
 * Error states for different component types
 */

/**
 * Props for the ComponentErrorFallback component
 * @interface ComponentErrorFallbackProps
 * 
 * @property {string} componentName - Name of the component that failed
 */
interface ComponentErrorFallbackProps {
  /** Name of the component that failed */
  componentName: string
}

/**
 * Generic component error fallback
 * @param {ComponentErrorFallbackProps} props - The component props 
 *  @param {string} props.componentName - Name of the component that failed
 * @returns {JSX.Element} - The generic error fallback component
 */
export const ComponentErrorFallback = ({ componentName }: ComponentErrorFallbackProps): JSX.Element => (
  <div className="p-4 rounded-md border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Component Error</h3>
        <div className="mt-2 text-sm text-red-700 dark:text-red-300">
          The {componentName} component failed to load.
        </div>
      </div>
    </div>
  </div>
)

/**
 * Image-specific error fallback
 * @returns {JSX.Element} - The image error fallback component
 */
export const ImageErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md flex items-center justify-center">
    <div className="text-center">
      <svg className="mx-auto h-10 w-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="mt-2 text-sm text-red-600 dark:text-red-400">Failed to load image</p>
    </div>
  </div>
)

/**
 * Chart-specific error fallback
 * @returns {JSX.Element} - The chart error fallback component
 */
export const ChartErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md flex items-center justify-center">
    <div className="text-center">
      <svg className="mx-auto h-10 w-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="mt-2 text-sm text-red-600 dark:text-red-400">Failed to load chart</p>
    </div>
  </div>
)

/**
 * Video-specific error fallback
 * @returns {JSX.Element} - The video error fallback component
 */
export const VideoErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md flex items-center justify-center">
    <div className="text-center">
      <svg className="mx-auto h-10 w-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="mt-2 text-sm text-red-600 dark:text-red-400">Failed to load video</p>
    </div>
  </div>
)

/**
 * Table-specific error fallback
 * @returns {JSX.Element} - The table error fallback component
 */
export const TableErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md flex items-center justify-center">
    <div className="text-center">
      <svg className="mx-auto h-10 w-10 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
      <p className="mt-2 text-sm text-red-600 dark:text-red-400">Failed to load table</p>
    </div>
  </div>
)

/**
 * Metrics-specific error fallback
 * @returns JSX.Element - The metrics error fallback component
 */
export const MetricsErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 my-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md flex items-center justify-center">
    <div className="text-center">
      <p className="text-red-600 dark:text-red-400 font-medium">Metrics failed to load</p>
      <p className="text-sm text-red-500 dark:text-red-300 mt-1">
        The metrics data could not be displayed
      </p>
    </div>
  </div>
)

/**
 * Callout-specific error fallback
 * @returns {JSX.Element} - The callout error fallback component
 */
export const CalloutErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 my-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm font-medium text-red-800 dark:text-red-200">Failed to load callout</p>
        <p className="mt-1 text-sm text-red-700 dark:text-red-300">The callout content could not be displayed.</p>
      </div>
    </div>
  </div>
)

/**
 * Tabs-specific error fallback
 * @returns {JSX.Element} - The tabs error fallback component
 */
export const TabsErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 my-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm font-medium text-red-800 dark:text-red-200">Failed to load tabs</p>
        <p className="mt-1 text-sm text-red-700 dark:text-red-300">The tabbed content could not be displayed.</p>
      </div>
    </div>
  </div>
)

/**
 * Accordion-specific error fallback
 * @returns {JSX.Element} - The accordion error fallback component
 */
export const AccordionErrorFallback = (): JSX.Element => (
  <div className="w-full p-4 my-4 border border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-900/20 rounded-md">
    <div className="flex">
      <div className="flex-shrink-0">
        <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
        </svg>
      </div>
      <div className="ml-3">
        <p className="text-sm font-medium text-red-800 dark:text-red-200">Failed to load accordion</p>
        <p className="mt-1 text-sm text-red-700 dark:text-red-300">The accordion content could not be displayed.</p>
      </div>
    </div>
  </div>
)

/**
 * Link-specific error fallback
 * @returns {JSX.Element} - The link error fallback component
 */
export const LinkErrorFallback = (): JSX.Element => (
  <span className="inline-block text-red-500 dark:text-red-400 border-b border-red-300 dark:border-red-600">
    <svg className="inline-block h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
    <span className="text-sm">Link error</span>
  </span>
)

/**
 * Inline-specific error fallback
 * @returns {JSX.Element} - The inline error fallback component
 */
export const InlineErrorFallback = (): JSX.Element => (
  <span className="inline-block text-red-500 dark:text-red-400 bg-red-50 dark:bg-red-900/10 px-1 py-0.5 rounded text-sm">
    <svg className="inline-block h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
    <span>Error</span>
  </span>
)

/**
 * Provider-specific error fallback
 * @returns {JSX.Element} - The provider error fallback component
 */
export const ProviderErrorFallback = (): JSX.Element => (
  <span className="sr-only">A provider component failed to load. This may affect functionality.</span>
)

/**
 * Utility-specific error fallback
 * @returns {JSX.Element} - The utility error fallback component
 */
export const UtilityErrorFallback = (): JSX.Element => (
  <span className="sr-only">A utility component failed to load. This may affect functionality.</span>
)

/**
 * Component category types for standardized fallbacks
 * 
 * @typedef {string} ComponentCategory - The category of the component  {image, chart, table, card, layout, metrics, interactive, media, text, quote}
 */
export type ComponentCategory = 
  | 'image'     // Image-based components
  | 'chart'     // Data visualization components
  | 'table'     // Tabular data components
  | 'card'      // Card-style UI components
  | 'layout'    // Layout and structural components
  | 'metrics'   // Metrics display components
  | 'interactive' // Interactive/complex components
  | 'media'     // Media components (video, audio)
  | 'text'      // Text-based components
  | 'quote'     // Quote components
  | 'link'      // Link components
  | 'inline'    // Inline components that need to avoid block-level elements
  | 'provider'  // Context providers and non-visual components
  | 'utility'   // Utility components with no visual representation

/**
 * Interface for components that support the disableDefaultWrapper property
 * Used to prevent paragraph wrapping in the MDX renderer
 * 
 * @interface ComponentWithDisableDefaultWrapper
 * @property {boolean} [disableDefaultWrapper] - Whether to disable the default my-8 wrapper (also used for loading fallbacks to prevent p tag wrapping)
 */
export interface ComponentWithDisableDefaultWrapper {
  disableDefaultWrapper?: boolean
}

/**
 * Selects the appropriate loading fallback component based on component category
 * 
 * @param {ComponentCategory} category - The category of the component
 * @param {string} componentName - The name of the component for display purposes
 * @param {ReactNode} [customFallback] - Optional custom fallback component
 * @returns {ComponentType<unknown>} The fallback component to use
 */
function getLoadingFallback(
  category: ComponentCategory, 
  componentName: string,
  customFallback?: ReactNode
): ComponentType<unknown> & ComponentWithDisableDefaultWrapper {
  if (customFallback) {
    // If a custom fallback is provided, use it
    const CustomFallback = function CustomFallbackComponent() {
      return customFallback as JSX.Element
    } as ComponentType<unknown> & ComponentWithDisableDefaultWrapper
    CustomFallback.displayName = `${componentName}LoadingFallback`
    CustomFallback.disableDefaultWrapper = true
    return CustomFallback
  }
  
  // Otherwise, select based on component category
  let FallbackComponent: ComponentType<unknown>
  
  switch (category) {
    case 'image':
      FallbackComponent = function ImageFallbackComponent() { return <ImageFallback /> }
      FallbackComponent.displayName = 'ImageFallbackComponent'
      break
    case 'chart':
      FallbackComponent = function ChartFallbackComponent() { return <ChartFallback /> }
      FallbackComponent.displayName = 'ChartFallbackComponent'
      break
    case 'table':
      FallbackComponent = function TableFallbackComponent() { return <TableFallback /> }
      FallbackComponent.displayName = 'TableFallbackComponent'
      break
    case 'card':
      FallbackComponent = function CardFallbackComponent() { return <CardFallback /> }
      FallbackComponent.displayName = 'CardFallbackComponent'
      break
    case 'metrics':
      FallbackComponent = function MetricsFallbackComponent() { return <MetricsFallback /> }
      FallbackComponent.displayName = 'MetricsFallbackComponent'
      break
    case 'media':
      FallbackComponent = function VideoFallbackComponent() { return <VideoFallback /> }
      FallbackComponent.displayName = 'VideoFallbackComponent'
      break
    case 'quote':
      FallbackComponent = function QuoteFallbackComponent() { return <QuoteFallback /> }
      FallbackComponent.displayName = 'QuoteFallbackComponent'
      break
    case 'interactive':
      FallbackComponent = function InteractiveFallbackComponent() { return <InteractiveFallback /> }
      FallbackComponent.displayName = 'InteractiveFallbackComponent'
      break
    case 'layout':
      FallbackComponent = function GridFallbackComponent() { return <GridFallback /> }
      FallbackComponent.displayName = 'GridFallbackComponent'
      break
    case 'link':
      FallbackComponent = function LinkFallbackComponent() { return <LinkFallback /> }
      FallbackComponent.displayName = 'LinkFallbackComponent'
      break
    case 'inline':
      FallbackComponent = function TextInlineFallbackComponent() { return <TextInlineFallback /> }
      FallbackComponent.displayName = 'TextInlineFallbackComponent'
      break
    case 'provider':
      FallbackComponent = function ProviderFallbackComponent() { return <ProviderFallback /> }
      FallbackComponent.displayName = 'ProviderFallbackComponent'
      break
    case 'utility':
      FallbackComponent = function UtilityFallbackComponent() { return <UtilityFallback /> }
      FallbackComponent.displayName = 'UtilityFallbackComponent'
      break
    case 'text':
    default:
      FallbackComponent = function TextFallbackComponent() { return <TextFallback /> }
      FallbackComponent.displayName = 'TextFallbackComponent'
      break
  }
  
  // Cast to the component type with our additional property
  const FallbackWithProperty = FallbackComponent as ComponentType<unknown> & ComponentWithDisableDefaultWrapper
  
  // Add static property to ALL fallback components to prevent p tag wrapping
  FallbackWithProperty.disableDefaultWrapper = true
  
  // Set a specific displayName for better debugging
  FallbackWithProperty.displayName = `${componentName}Loading`
  return FallbackWithProperty
}

/**
 * Selects the appropriate error fallback component based on component category
 * 
 * @param {ComponentCategory} category - The category of the component
 * @param {ReactNode} [customFallback] - Optional custom fallback component
 * @returns {ComponentType<{ componentName: string }>} The error fallback component to use
 */
function getErrorFallback(
  category: ComponentCategory, 
  customFallback?: ReactNode
): ComponentType<{ componentName: string }> {
  if (customFallback) {
    // If a custom fallback is provided, use it
    const CustomErrorFallback = function CustomErrorFallbackComponent(): JSX.Element {
      return customFallback as JSX.Element
    }
    CustomErrorFallback.displayName = 'CustomErrorFallbackComponent'
    return CustomErrorFallback
  }
  
  // Define all error fallback components outside of the switch statement
  const ImageErrorFallbackComponent = function ImageErrorFallbackComponent(): JSX.Element { 
    return <ImageErrorFallback /> 
  }
  ImageErrorFallbackComponent.displayName = 'ImageErrorFallbackComponent'
  
  const ChartErrorFallbackComponent = function ChartErrorFallbackComponent(): JSX.Element { 
    return <ChartErrorFallback /> 
  }
  ChartErrorFallbackComponent.displayName = 'ChartErrorFallbackComponent'
  
  const TableErrorFallbackComponent = function TableErrorFallbackComponent(): JSX.Element { 
    return <TableErrorFallback /> 
  }
  TableErrorFallbackComponent.displayName = 'TableErrorFallbackComponent'
  
  const MetricsErrorFallbackComponent = function MetricsErrorFallbackComponent(): JSX.Element { 
    return <MetricsErrorFallback /> 
  }
  MetricsErrorFallbackComponent.displayName = 'MetricsErrorFallbackComponent'
  
  const VideoErrorFallbackComponent = function VideoErrorFallbackComponent(): JSX.Element { 
    return <VideoErrorFallback /> 
  }
  VideoErrorFallbackComponent.displayName = 'VideoErrorFallbackComponent'
  
  const LinkErrorFallbackComponent = function LinkErrorFallbackComponent(): JSX.Element { 
    return <LinkErrorFallback /> 
  }
  LinkErrorFallbackComponent.displayName = 'LinkErrorFallbackComponent'
  
  const InlineErrorFallbackComponent = function InlineErrorFallbackComponent(): JSX.Element { 
    return <InlineErrorFallback /> 
  }
  InlineErrorFallbackComponent.displayName = 'InlineErrorFallbackComponent'
  
  const ProviderErrorFallbackComponent = function ProviderErrorFallbackComponent(): JSX.Element { 
    return <ProviderErrorFallback /> 
  }
  ProviderErrorFallbackComponent.displayName = 'ProviderErrorFallbackComponent'
  
  const UtilityErrorFallbackComponent = function UtilityErrorFallbackComponent(): JSX.Element { 
    return <UtilityErrorFallback /> 
  }
  UtilityErrorFallbackComponent.displayName = 'UtilityErrorFallbackComponent'
  
  switch (category) {
    case 'image':
      return ImageErrorFallbackComponent
    case 'chart':
      return ChartErrorFallbackComponent
    case 'table':
      return TableErrorFallbackComponent
    case 'metrics':
      return MetricsErrorFallbackComponent
    case 'media':
      return VideoErrorFallbackComponent
    case 'link':
      return LinkErrorFallbackComponent
    case 'inline':
      return InlineErrorFallbackComponent
    case 'provider':
      return ProviderErrorFallbackComponent
    case 'utility':
      return UtilityErrorFallbackComponent
    case 'layout':
      return ComponentErrorFallback
    case 'text':
      return ComponentErrorFallback
    case 'card':
      return ComponentErrorFallback
    case 'quote':
      return ComponentErrorFallback
    case 'interactive':
      return ComponentErrorFallback
    default:
      return ComponentErrorFallback
  }
}

/**
 * ==========================================
 * COMPONENT TRANSFORMERS
 * ==========================================
 */

/**
 * Creates a transformer that wraps a component in a div with a standardized class name.
 * Useful for consistent spacing across MDX components.
 * 
 * @param {string} wrapperClassName - The class name to add to the wrapper div
 * @returns {<P extends JSX.IntrinsicAttributes>(Component: ComponentType<P>) => ComponentType<P>} Transformed component
 */
export function createStandardWrapper<WC extends string = "my-8">(
  wrapperClassName: WC = "my-8" as WC
) {
  // Returns a FunctionComponent (not ComponentType): React 19's ComponentType
  // unions in ClassComponent, which is no longer assignable to mdx/types'
  // `Component<any>` value position in the MDX component registry. The wrapper
  // is always a function component, so this annotation is both accurate and
  // compatible with `MDXComponents`.
  return function standardWrapper<P extends JSX.IntrinsicAttributes>(
    Component: ComponentType<P>
  ): FunctionComponent<P> {
    const WrappedComponent = (props: P): JSX.Element => (
      <div className={wrapperClassName}>
        <Component {...props} />
      </div>
    )
    
    // Preserve display name for debugging
    WrappedComponent.displayName = `StandardWrapper(${
      Component.displayName ?? Component.name ?? "Component"
    })`
    
    return WrappedComponent
  }
}

/**
 * Creates a transformer that merges class names for a component.
 * Preserves any passed className while adding the base class name.
 * 
 * @param {string} baseClassName - The base class name to always include
 * @returns {<P extends { className?: string } & JSX.IntrinsicAttributes>(Component: ComponentType<P>) => ComponentType<P>} Transformed component
 */
export function createClassNameTransformer(baseClassName: string) {
  return function classNameTransformer<P extends { className?: string } & JSX.IntrinsicAttributes>(
    Component: ComponentType<P>
  ): ComponentType<P> {
    // Use a generic that extends P to ensure type safety
    const TransformedComponent = (props: P): JSX.Element => {
      // Create a new props object with the merged className
      // This approach avoids type assertions to 'any'
      const newProps = Object.assign({}, props, {
        className: cn(baseClassName, props.className)
      }) as P
      
      return <Component {...newProps} />
    }
    
    // Preserve display name for debugging
    TransformedComponent.displayName = `WithClassName(${
      Component.displayName ?? Component.name ?? "Component"
    })`
    
    return TransformedComponent
  }
}

/**
 * Higher-order component that wraps components with standard spacing.
 * 
 * @template P - The props type of the wrapped component
 * @param {React.ComponentType<P>} C - The component to wrap
 * @returns {React.ComponentType<P>} The wrapped component with standard spacing
 */
export const withStandardWrapper = <P extends object>(
  C: React.ComponentType<P>
): React.ComponentType<P> => {
  const WrappedComponent: React.ComponentType<P> = (props: P) => (
    <div className="my-8">
      <C {...props} />
    </div>
  )
  WrappedComponent.displayName = `withStandardWrapper(${C.displayName ?? C.name ?? 'Component'})`
  return WrappedComponent
}

/**
 * ==========================================
 * CONTENT VALIDATION
 * ==========================================
 * Helper functions specific to heading validation.
 * These functions are ONLY for use with h1-h6 elements to prevent
 * frontmatter and metadata from being rendered as headings.
 */

/**
 * Validates if the content is appropriate for heading elements ONLY.
 * This is specifically for h1-h6 elements to prevent frontmatter and metadata
 * from being rendered as headings. DO NOT use this for other components
 * as it will incorrectly filter out valid JSX/MDX content.
 * 
 * @param {unknown} content - The heading content to validate
 * @returns {boolean} Boolean indicating if content is valid for heading
 */
export function isValidHeadingContent(content: unknown): boolean {
  if (typeof content !== 'string') return true
  
  const text = content.trim()
  
  // Filter out frontmatter markers
  if (text.startsWith('---') || text.endsWith('---')) return false
  
  // Filter out JSON-like metadata
  if (text.includes('{') && text.includes('}') && 
      (text.includes('"') || text.includes("'") || text.includes(':'))) return false
  
  // Filter out long metadata-like content
  if (text.length > 100 && (text.includes(':') || text.includes('{'))) return false

  return true
}

/**
 * Generate a URL-friendly ID from heading content for anchor links
 * Used to create in-page navigation (table of contents) and linkable sections
 * 
 * @param {unknown} content - The content to generate an ID from
 * @returns {string | undefined} The generated ID or undefined if invalid
 */
export function generateHeadingId(content: unknown): string | undefined {
  // Skip generation for non-string content
  if (typeof content !== 'string') return undefined
  
  // Convert to kebab-case for URL compatibility
  return content
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/--+/g, '-')     // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, '')  // Remove leading and trailing hyphens
    .substring(0, 100)        // Limit length
}

/**
 * Props for Heading component
 * @interface HeadingProps
 * @extends HTMLAttributes<HTMLHeadingElement>
 * @property {1|2|3|4|5|6} level - The heading level (h1-h6)
 *  @property {ReactNode} children - The heading content (inherited from HTMLAttributes)
 *  @property {string} [className] - Additional CSS classes (inherited from HTMLAttributes)
 */
export interface HeadingProps extends HTMLAttributes<HTMLHeadingElement> {
  /** The heading level (h1-h6) */
  level: 1 | 2 | 3 | 4 | 5 | 6
}

/**
 * Renders an accessible heading with the specified level (h1-h6)
 * Includes auto-generated anchor links for easier navigation and sharing
 * 
 * @param {Object} props - The heading component props
 *   @param {1 | 2 | 3 | 4 | 5 | 6} props.level - The heading level (h1-h6 as numbers)
 *   @param {ReactNode} props.children - The heading content
 *   @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element | null} The heading component or null if content is invalid
 */
type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6

/**
 * Shifts a markdown heading level by an offset, clamped to h6.
 *
 * @param {HeadingLevel} baseLevel - Source heading level from MDX.
 * @param {number} offset - Levels to add (e.g. 1 when the page template already owns the h1).
 * @returns {HeadingLevel} Adjusted heading level.
 */
function shiftHeadingLevel(baseLevel: HeadingLevel, offset: number): HeadingLevel {
  return Math.min(6, baseLevel + offset) as HeadingLevel
}

/**
 * Builds MDX heading component overrides that demote content headings when a
 * page template already renders the document h1 (blog post header, legal layout).
 *
 * @param {number} [offset=0] - Levels to add to each markdown heading.
 * @returns {Record<string, (props: HeadingProps) => JSX.Element | null>} MDX heading map.
 */
export function createHeadingComponents(offset = 0): Record<string, (props: HeadingProps) => JSX.Element | null> {
  const withLevel = (baseLevel: HeadingLevel, { level: _ignored, ...props }: HeadingProps): JSX.Element | null => (
    <Heading level={shiftHeadingLevel(baseLevel, offset)} {...props} />
  )
  return {
    h1: (props) => withLevel(1, props),
    h2: (props) => withLevel(2, props),
    h3: (props) => withLevel(3, props),
    h4: (props) => withLevel(4, props),
    h5: (props) => withLevel(5, props),
    h6: (props) => withLevel(6, props),
  }
}

export function Heading({ level, children, className, ...props }: HeadingProps): JSX.Element | null {
  // Skip rendering if content isn't valid for headings (prevents metadata rendering)
  if (!isValidHeadingContent(children)) {
    return null
  }
  
  // Generate an ID for anchor linking
  const Component = `h${level}` as const
  const id = generateHeadingId(children)

  // Updated heading styles with improved spacing
  const styles = {
    1: "mt-12 scroll-m-32 text-4xl font-bold tracking-tight first:mt-0",
    2: "mt-16 scroll-m-32 border-b pb-4 text-3xl font-semibold tracking-tight first:mt-0",
    3: "mt-12 scroll-m-32 text-2xl font-semibold tracking-tight",
    4: "mt-10 scroll-m-28 text-xl font-semibold tracking-tight",
    5: "mt-8 scroll-m-20 text-lg font-semibold tracking-tight",
    6: "mt-8 scroll-m-20 text-base font-semibold tracking-tight"
  }

  return (
    <Component
      id={id}
      className={cn(styles[level], className)}
      {...props}
    >
      {children}
    </Component>
  )
}

/**
 * Safely checks if an object has a boolean property with value true
 * 
 * @param {unknown} obj - The object to check
 * @param {string} prop - The property name to check for
 * @returns {boolean} True if the property exists and is true, false otherwise
 */
export function hasTruthyProp(obj: unknown, prop: string): boolean {
  if (obj && typeof obj === 'object' && prop in obj) {
    const value = (obj as Record<string, unknown>)[prop]
    return value === true
  }
  return false
}

/**
 * Safely gets a string property from an object
 * 
 * @param {unknown} obj - The object to check
 * @param {string} prop - The property name to get
 * @returns {string} The string value or empty string if not found/not a string
 */
export function getStringProp(obj: unknown, prop: string): string {
  if (obj && typeof obj === 'object' && prop in obj) {
    const value = (obj as Record<string, unknown>)[prop]
    return typeof value === 'string' ? value : ''
  }
  return ''
}

/**
 * Collection of utility functions and components for MDX rendering
 */
export const componentUtils = {
  // Fallbacks
  ImageFallback,
  QuoteFallback,
  ChartFallback,
  VideoFallback,
  CardFallback,
  TableFallback,
  MetricsFallback,
  TextInlineFallback,
  
  // Error fallbacks
  ComponentErrorFallback,
  ImageErrorFallback,
  ChartErrorFallback,
  VideoErrorFallback,
  TableErrorFallback,
  MetricsErrorFallback,
  
  // Helper functions
  logComponentError,
  isValidHeadingContent,
  generateHeadingId,
  hasTruthyProp,
  getStringProp,
  
  // Components
  Heading,
  getLoadingFallback,
  getErrorFallback,
  createStandardWrapper,
  createClassNameTransformer
}

// Export the utilities as the default export
export default componentUtils